/**
 * Color Dropdown jQuery Plugin
 * Handles accessible color dropdown functionality
 * @module colorDropdown
 */

import $ from 'jquery';
import registerJQueryPlugin from 'styleguide/lib/register-jquery-plugin';
import mergeOptions from 'styleguide/lib/merge-options';

// Plugin name and default options
export const PLUGIN_NAME = 'colorDropdown';
export const DEFAULTS = {
  colorDropdown: '.js-color-dropdown',
  colorDropdownButton: '.color-dropdown__button',
  colorDropdownList: '.color-dropdown__list',
  colorDropdownOption: '.color-dropdown__option[role="option"]',
  activeClass: 'show',
  hiddenSelect: 'select',
  animationDelay: 50
};

/**
 * Sets focus to the selected option when dropdown opens
 * @param {jQuery} $container - Dropdown container element
 * @param {Object} options - Plugin options
 */
function focusSelectedOption($container, options) {
  const $list = $container.find(options.colorDropdownList);
  const $selectedOption = $list.find('[aria-selected="true"]');
  
  if ($selectedOption.length) {
    // Small delay to ensure DOM is ready and transitions are complete
    setTimeout(() => {
      $selectedOption.focus();
    }, options.animationDelay);
  }
}

/**
 * Opens the color dropdown
 * @param {jQuery} $container - Dropdown container element
 * @param {Object} options - Plugin options
 */
function openDropdown($container, options) {
  const $button = $container.find(options.colorDropdownButton);
  const $list = $container.find(options.colorDropdownList);
  
  // Store the container for focus management
  $list.data('container', $container);
  
  // Set open state
  $button.attr('aria-expanded', 'true');
  $list.addClass(options.activeClass);
  
  // Focus selected option
  focusSelectedOption($container, options);
}

/**
 * Closes the color dropdown
 * @param {jQuery} $container - Dropdown container element
 * @param {Object} options - Plugin options
 */
function closeDropdown($container, options) {
  const $button = $container.find(options.colorDropdownButton);
  const $list = $container.find(options.colorDropdownList);
  
  // Remove open state
  $button.attr('aria-expanded', 'false');
  $list.removeClass(options.activeClass);
  
  // Clean up stored data
  $list.removeData('container');
}

/**
 * Toggles the dropdown open/closed state
 * @param {jQuery} $container - Dropdown container element
 * @param {Object} options - Plugin options
 */
function toggleDropdown($container, options) {
  const $button = $container.find(options.colorDropdownButton);
  const isOpen = $button.attr('aria-expanded') === 'true';
  
  if (isOpen) {
    closeDropdown($container, options);
  } else {
    openDropdown($container, options);
  }
}

/**
 * Selects a color option and updates the display
 * @param {jQuery} $container - Dropdown container element
 * @param {jQuery} $selectedOption - The option that was selected
 * @param {Object} options - Plugin options
 */
function selectOption($container, $selectedOption, options) {
  const $button = $container.find(options.colorDropdownButton);
  const $options = $container.find(options.colorDropdownOption);
  const $hiddenSelect = $container.find(options.hiddenSelect);
  
  // Update aria-selected
  $options.attr('aria-selected', 'false');
  $selectedOption.attr('aria-selected', 'true');

  // Update button display
  const colorClass = $selectedOption.data('color');
  const text = $selectedOption.text().trim();
  
  const $selectedColorCircle = $button.find('.color-dropdown__color-selected');
  const $selectedText = $button.find('.color-dropdown__option span:nth-child(2)');
  
  $selectedColorCircle.attr('class', `color-dropdown__color-selected ${colorClass}`);
  $selectedText.text(text);

  // Update hidden select
  $hiddenSelect.val($selectedOption.data('value'));

  // Update tabindex for keyboard navigation
  $options.attr('tabindex', '-1');
  $selectedOption.attr('tabindex', '0');

  // Close dropdown and return focus to button
  closeDropdown($container, options);
  
  // Small delay to ensure dropdown is closed before focusing
  setTimeout(() => {
    $button.focus();
  }, options.animationDelay);
}

/**
 * Navigates through options using arrow keys
 * @param {jQuery} $options - All dropdown options
 * @param {string} direction - 'up' or 'down'
 */
function navigateOptions($options, direction) {
  const $focused = $options.filter(':focus');
  const currentIndex = $options.index($focused);
  let newIndex;

  if (direction === 'down') {
    newIndex = currentIndex + 1;
    if (newIndex >= $options.length) newIndex = 0;
  } else {
    newIndex = currentIndex - 1;
    if (newIndex < 0) newIndex = $options.length - 1;
  }

  $options.eq(newIndex).focus();
}

/**
 * Handles click events outside the dropdown to close it
 * @param {Event} e - Click event
 * @param {jQuery} $container - Dropdown container element
 * @param {Object} options - Plugin options
 */
function handleOutsideClick(e, $container, options) {
  if (!$container.is(e.target) && $container.has(e.target).length === 0) {
    closeDropdown($container, options);
  }
}

/**
 * Binds all event handlers for the dropdown
 * @param {jQuery} $container - Dropdown container element
 * @param {Object} options - Plugin options
 */
function bindEventHandlers($container, options) {
  const $button = $container.find(options.colorDropdownButton);
  const $options = $container.find(options.colorDropdownOption);
  
  // Remove any previously bound events to prevent duplicates
  $(document).off('click.colorDropdown');
  $(document).off('keydown.colorDropdown');
  $container.off('.colorDropdown');
  
  // Button click event
  $button.on('click.colorDropdown', function() {
    toggleDropdown($container, options);
  });

  // Button keyboard events
  $button.on('keydown.colorDropdown', function(e) {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      openDropdown($container, options);
    }
  });

  // Option click events
  $options.on('click.colorDropdown', function() {
    const $option = $(this);
    selectOption($container, $option, options);
  });

  // Option keyboard events
  $options.on('keydown.colorDropdown', function(e) {
    const $option = $(this);
    
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      selectOption($container, $option, options);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      navigateOptions($options, 'down');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      navigateOptions($options, 'up');
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown($container, options);
      $button.focus();
    }
  });

  // Global event handlers
  $(document).on('click.colorDropdown', function(e) {
    handleOutsideClick(e, $container, options);
  });

  // Handle escape key globally
  $(document).on('keydown.colorDropdown', function(e) {
    if (e.key === 'Escape') {
      const $button = $container.find(options.colorDropdownButton);
      const isOpen = $button.attr('aria-expanded') === 'true';
      if (isOpen) {
        closeDropdown($container, options);
        $button.focus();
      }
    }
  });
}

/**
 * Sets up accessibility attributes on dropdown elements
 * @param {jQuery} $container - Dropdown container element
 * @param {Object} options - Plugin options
 */
function initializeA11yAttributes($container, options) {
  const $button = $container.find(options.colorDropdownButton);
  const $list = $container.find(options.colorDropdownList);
  const $options = $container.find(options.colorDropdownOption);
  
  // Set up button accessibility
  if (!$button.attr('aria-haspopup')) {
    $button.attr('aria-haspopup', 'listbox');
  }
  
  if (!$button.attr('aria-expanded')) {
    $button.attr('aria-expanded', 'false');
  }
  
  // Set up list accessibility
  if (!$list.attr('role')) {
    $list.attr('role', 'listbox');
  }
  
  // Generate unique IDs for aria-controls if needed
  let listId = $list.attr('id');
  if (!listId) {
    listId = `color-dropdown-list-${Math.random().toString(36).substr(2, 9)}`;
    $list.attr('id', listId);
  }
  
  $button.attr('aria-controls', listId);
  
  // Set up option accessibility
  $options.each(function() {
    const $option = $(this);
    if (!$option.attr('tabindex')) {
      const isSelected = $option.attr('aria-selected') === 'true';
      $option.attr('tabindex', isSelected ? '0' : '-1');
    }
  });
}

/**
 * Main plugin initialization function
 * @param {HTMLElement} el - The dropdown container element
 * @param {Object} userOptions - User-provided options
 * @returns {jQuery} The jQuery element for chaining
 */
export default function colorDropdown(el, userOptions = {}) {
  const $element = $(el);
  
  // Prevent multiple initializations
  if ($element.data(PLUGIN_NAME + 'Initialized')) {
    return $element;
  }
  
  // Merge default options with user options
  const options = mergeOptions(DEFAULTS, userOptions, $element);
  
  // Initialize accessibility attributes
  initializeA11yAttributes($element, options);
  
  // Bind event handlers
  bindEventHandlers($element, options);
  
  // Mark as initialized
  $element.data(PLUGIN_NAME + 'Initialized', true);
  
  // Store options for later access
  $element.data(PLUGIN_NAME + 'Options', options);
  
  // Return element for chaining
  return $element;
}

// Register as jQuery plugin
registerJQueryPlugin(PLUGIN_NAME, colorDropdown);


// WEBPACK FOOTER //
// ./src/scripts/entry-points/pdp/color-dropdown.js